// Ekran 03 — numune detay sekmesi (pane#pane-n-<NUM>).
// Tam analiz geçmişi + revizyon (K15): satıra tıklayınca form düzenleme
// moduna geçer, gerekçe zorunlu olur ve revizyon sayacı artar.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'
import { tr, trTarihSaat } from '../../lib/trFormat'
import { useYetki } from '../../lib/yetki'
import AnalizFormu from './AnalizFormu'
import {
  DURUM_ROZET,
  GubreDegeriKarti,
  ProsesSagligiKarti,
  TIP_ETIKET,
  kaynakMetni,
  kisaTarih,
  kisaZaman,
  sonAnaliz,
} from './LaboratuvarSayfasi'
import {
  analizler as analizApi,
  numuneler as numuneApi,
  type AnalizAlanlari,
  type AnalizDto,
  type NumuneTipi,
} from './api'

/** Analiz tablosunun sütunları tipe göre değişir (ekran 03 .ptab varyantları). */
const SUTUNLAR: Record<NumuneTipi, { baslik: string; alan: keyof AnalizDto; basamak: number }[]> = {
  lot: [
    { baslik: 'KM %', alan: 'km', basamak: 2 },
    { baslik: 'OKM %KM', alan: 'okm', basamak: 2 },
    { baslik: 'Kül %KM', alan: 'kul', basamak: 2 },
    { baslik: 'pH', alan: 'ph', basamak: 2 },
  ],
  fermenter: [
    { baslik: 'pH', alan: 'ph', basamak: 2 },
    { baslik: 'FOS/TAC', alan: 'fosTac', basamak: 2 },
    { baslik: 'VFA', alan: 'vfa', basamak: 0 },
    { baslik: 'NH₄-N', alan: 'nh4', basamak: 0 },
    { baslik: 'KM %', alan: 'km', basamak: 2 },
    { baslik: 'oKM %KM', alan: 'okm', basamak: 2 },
  ],
  urun: [
    { baslik: 'Nem %', alan: 'nem', basamak: 1 },
    { baslik: 'N kg/t', alan: 'n', basamak: 1 },
    { baslik: 'P₂O₅ kg/t', alan: 'p', basamak: 1 },
    { baslik: 'K₂O kg/t', alan: 'k', basamak: 1 },
  ],
}

/** Referans av(f) (ref:670): boş olmayan ölçümlerin ortalaması; hiç yoksa null. */
function ortalama(analizler: AnalizDto[], alan: keyof AnalizDto): number | null {
  const v = analizler
    .map((a) => a[alan])
    .filter((x): x is number => typeof x === 'number')
  return v.length > 0 ? v.reduce((t, x) => t + x, 0) / v.length : null
}

export default function NumuneSekmesi({ id }: { id: string }) {
  const kuyruk = useQueryClient()
  const { izinVar } = useYetki()
  const yazabilir = izinVar('Laboratuvar', 'yaz')
  const [duzenlenenId, setDuzenlenenId] = useState<string | null>(null)

  const { data: detay } = useQuery({
    queryKey: ['numune', id],
    queryFn: () => numuneApi.detay(id),
  })

  // Fermenter formunun canlı rozetleri §6.3 eşiklerini okuyor; liste ekranıyla
  // AYNI anahtar kullanıldığı için ek istek çıkmıyor, önbellek paylaşılıyor.
  const { data: ozet } = useQuery({ queryKey: ['lab-ozeti'], queryFn: numuneApi.ozet })

  function tazele() {
    kuyruk.invalidateQueries({ queryKey: ['numune'] })
    kuyruk.invalidateQueries({ queryKey: ['numuneler'] })
    kuyruk.invalidateQueries({ queryKey: ['lab-ozeti'] })
    kuyruk.invalidateQueries({ queryKey: ['havuzlar'] })
  }

  const kaydet = useMutation({
    mutationFn: ({ alanlar, gerekce }: { alanlar: AnalizAlanlari; gerekce?: string }) =>
      duzenlenenId && gerekce
        ? analizApi.revize(duzenlenenId, alanlar, gerekce)
        : analizApi.ekle(id, alanlar),
    onSuccess: (s) => { tazele(); setDuzenlenenId(null); toast(s.mesaj) },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Analiz kaydedilemedi'),
  })

  if (!detay) return <div className="cell-sub" style={{ padding: 14 }}>Yükleniyor…</div>

  const n = detay.numune
  const k = kaynakMetni(n)
  const sutunlar = SUTUNLAR[n.tip]
  const duzenlenen = detay.analizler.find((a) => a.id === duzenlenenId) ?? null

  const son = sonAnaliz(detay)
  const ort = (alan: keyof AnalizDto) => ortalama(detay.analizler, alan)

  // KPI değerinin rengi son analizin bandından gelir (ref kc(f)); rozet
  // sınıfları değil, `.k-v` varyantları kullanılır.
  const kpiSinifi = (alan: string) => {
    const bant = detay.bantlar.find((b) => b.alan === alan)?.bant
    return bant === 'kritik' ? 'k-v red' : bant === 'izle' ? 'k-v amber' : 'k-v'
  }

  // Kül literatürü kartta ayrı tutulmuyor; referans da 100 − oKM ile türetiyor (ref:685).
  const okmLiteraturu = detay.kmKiyas?.stokKartiOkm ?? null
  const kulLiteraturu = okmLiteraturu === null ? null : 100 - okmLiteraturu

  return (
    <>
      <div className="crumb">
        Laboratuvar / Numuneler <b>/ {n.no}</b>
      </div>

      <div className="d-head">
        <h1 className="d-title">{n.no}</h1>
        {/* Referans lotta "kabul fişi · lot" basıyor (ref:675); kabul fişi no
            §5.1'de yok (kararlar-ek.md:1766), yerine lot belge no geçiyor. */}
        <span className="d-kod">
          {n.tip === 'lot' ? (n.lotBelgeNo ?? TIP_ETIKET[n.tip]) : TIP_ETIKET[n.tip]}
        </span>
        <span className={DURUM_ROZET[n.durum].sinif}>{DURUM_ROZET[n.durum].metin}</span>
        {/* Kaynak · (lotta cari) · alınma — referansın dördüncü çocuğu (ref:677) */}
        <span className="tur-t">
          {k.ust}
          {n.tip === 'lot' && n.cariUnvan ? ` · ${n.cariUnvan}` : ''} · alınma{' '}
          {kisaZaman(n.alinmaZamani)}
        </span>
      </div>

      {/* Tipe göre üç ayrı KPI seti (ref:681-686 · 702-708 · 719-725) */}
      <div className="kstrip">
        <div className="k">
          <div className="k-l">Analiz Sayısı</div>
          <div className="k-v">{detay.analizler.length}</div>
          {/* Fermenter'de ünite, üründe ürün adı yazar (ref:703 · 721) */}
          <div className="k-s">{n.tip === 'lot' ? 'sınırsız eklenebilir' : k.ust}</div>
        </div>

        {n.tip === 'lot' && (
          <>
            <div className="k">
              <div className="k-l">KM Ort.</div>
              <div className="k-v">{tr(ort('km'), 2)}</div>
              <div className="k-s">literatür {tr(detay.kmKiyas?.stokKarti ?? null, 2)}</div>
            </div>
            <div className="k">
              <div className="k-l">OKM Ort.</div>
              <div className="k-v">{tr(ort('okm'), 2)}</div>
              <div className="k-s">literatür {tr(okmLiteraturu, 2)}</div>
            </div>
            <div className="k">
              <div className="k-l">Kül Ort.</div>
              <div className="k-v">{tr(ort('kul'), 2)}</div>
              <div className="k-s">literatür {tr(kulLiteraturu, 2)}</div>
            </div>
            <div className="k">
              <div className="k-l">pH Ort.</div>
              <div className="k-v">{tr(ort('ph'), 2)}</div>
              <div className="k-s">numune ortalaması</div>
            </div>
          </>
        )}

        {n.tip === 'fermenter' && (
          <>
            <div className="k">
              <div className="k-l">pH Son</div>
              <div className={kpiSinifi('ph')}>{tr(son?.ph ?? null, 2)}</div>
              <div className="k-s">hedef 7,0–7,8</div>
            </div>
            <div className="k">
              <div className="k-l">FOS/TAC Son</div>
              <div className={kpiSinifi('fosTac')}>{tr(son?.fosTac ?? null, 2)}</div>
              <div className="k-s">sağlıklı ≤0,4</div>
            </div>
            <div className="k">
              <div className="k-l">NH₄-N Son</div>
              <div className={kpiSinifi('nh4')}>{tr(son?.nh4 ?? null, 0)}</div>
              <div className="k-s">mg/L · sınır 3000</div>
            </div>
            <div className="k">
              <div className="k-l">VFA Son</div>
              <div className={kpiSinifi('vfa')}>{tr(son?.vfa ?? null, 0)}</div>
              <div className="k-s">mg/L · sınır 1500</div>
            </div>
          </>
        )}

        {n.tip === 'urun' && (
          <>
            <div className="k">
              <div className="k-l">Nem Ort.</div>
              <div className="k-v">{tr(ort('nem'), 1)}</div>
              <div className="k-s">% · satış şartnamesi</div>
            </div>
            <div className="k">
              <div className="k-l">N Ort.</div>
              <div className="k-v">{tr(ort('n'), 1)}</div>
              <div className="k-s">kg/t</div>
            </div>
            <div className="k">
              <div className="k-l">P₂O₅ Ort.</div>
              <div className="k-v">{tr(ort('p'), 1)}</div>
              <div className="k-s">kg/t</div>
            </div>
            <div className="k">
              <div className="k-l">K₂O Ort.</div>
              <div className="k-v">{tr(ort('k'), 1)}</div>
              <div className="k-s">kg/t</div>
            </div>
          </>
        )}
      </div>

      <div className="d-grid">
        <div className="card">
          <div className="sect-t">
            Analizler ({detay.analizler.length}) — düzenlemek için satıra tıklayın
          </div>
          {/* Referans bu tabloyu `.ptab` ile basıyor (ref:687 · 708 · 725);
              `.grid` sınıfı YOK — başlıkların 480px'lik sütunda sarabilmesi
              için nowrap kuralı da gelmemeli. Kayıt yokken tablo (başlık satırı
              dahil) TÜMÜYLE `.insp-empty` ile değişir (ref:688 · 709 · 726). */}
          {detay.analizler.length === 0 ? (
            <div className="insp-empty">Henüz analiz girilmedi — sağdaki formdan ekleyin.</div>
          ) : (
            <table className="ptab">
              <thead>
                <tr>
                  <th style={{ width: 52 }}>#</th>
                  <th style={{ width: 62 }}>Tarih</th>
                  {/* Analist yalnız LOT varyantında var (ref:687). */}
                  {n.tip === 'lot' && <th style={{ width: 82 }}>Analist</th>}
                  {sutunlar.map((s) => (
                    <th className="num" key={s.alan as string}>{s.baslik}</th>
                  ))}
                  <th style={{ width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {detay.analizler.map((a) => (
                  <tr
                    key={a.id}
                    className={duzenlenenId === a.id ? 'sel' : ''}
                    onClick={() => yazabilir && setDuzenlenenId(a.id)}
                  >
                    <td className="mono">
                      #{a.siraNo}
                      {a.revizyonNo > 0 && (
                        <span className="badge b-gray" style={{ marginLeft: 4 }}>
                          rev {a.revizyonNo}
                        </span>
                      )}
                    </td>
                    {/* Satırın kendi kayıt tarihi — 62px'e yalnız gün.ay sığıyor,
                        tam zaman ipucunda duruyor. */}
                    <td className="mono" style={{ fontSize: 11 }} title={trTarihSaat(a.zaman)}>
                      {kisaTarih(a.zaman)}
                    </td>
                    {n.tip === 'lot' && <td>{a.analistAd ?? '—'}</td>}
                    {sutunlar.map((s) => (
                      <td className="num" key={s.alan as string}>
                        {tr(a[s.alan] as number | null, s.basamak)}
                      </td>
                    ))}
                    <td>
                      <div className="row-acts">
                        <button
                          className="mini-btn"
                          type="button"
                          disabled={!yazabilir}
                          title={yazabilir ? 'Revizyon (K15)' : 'Yazma yetkiniz yok'}
                          onClick={(e) => { e.stopPropagation(); setDuzenlenenId(a.id) }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {detay.analizler.some((a) => a.revizyonGerekce) && (
            <div style={{ padding: '0 12px 12px' }}>
              <div className="insp-sec-t">Revizyon Gerekçeleri</div>
              <div className="mini-list">
                {detay.analizler
                  .filter((a) => a.revizyonGerekce)
                  .map((a) => (
                    <div className="mini-li" key={a.id}>
                      #{a.siraNo} · rev {a.revizyonNo}
                      <span className="vv">{a.revizyonGerekce}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="card">
            {/* Sekmede başlık ek ALMAZ (ref:611) — `kaynakAd` bilerek geçilmiyor. */}
            <AnalizFormu
              tip={n.tip}
              siraNo={detay.analizler.length + 1}
              duzenlenen={duzenlenen}
              bekliyor={kaydet.isPending}
              yazabilir={yazabilir}
              esikler={ozet?.esikler}
              onKaydet={(alanlar, gerekce) => kaydet.mutate({ alanlar, gerekce })}
              onVazgec={() => setDuzenlenenId(null)}
            />
            <div className="pnote">
              Eski kayıtlar düzenlenebilir; her değişiklik <b>denetim günlüğüne</b> işlenir (rev).
            </div>
          </div>

          {/* Koyu kart tipe göre değişir: lot → KM Kıyas, fermenter → Proses
              Sağlığı, ürün → Gübre Değeri (ref:733 · 710 · 727). Sekmede
              `.insp-sec` sarmalı YOKTUR. */}
          {n.tip === 'fermenter' && (
            <ProsesSagligiKarti detay={detay} bosNot="Veri bekleniyor" stil={{ marginTop: 10 }} />
          )}

          {/* Aynı ünitenin son ölçümleri (ref:714-715) */}
          {n.tip === 'fermenter' && (
            <div className="side-card">
              <div className="sect-t">FOS/TAC &amp; pH Trendi</div>
              <div className="mini-list" style={{ padding: '4px 0' }}>
                {detay.fosTacTrendi.length === 0 ? (
                  <div className="mini-li" style={{ padding: '5px 12px', color: 'var(--faint)' }}>
                    Veri yok
                  </div>
                ) : (
                  detay.fosTacTrendi.map((t, i) => (
                    <div className="mini-li" style={{ padding: '5px 12px' }} key={i}>
                      <span className="dt">{kisaZaman(t.zaman)}</span>
                      pH {tr(t.ph, 2)}
                      <span className={t.bant === 'saglikli' ? 'vv' : 'vv minus'}>
                        {tr(t.fosTac, 2)}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {n.tip === 'urun' && (
            <GubreDegeriKarti detay={detay} bosNot="Veri bekleniyor" stil={{ marginTop: 10 }} />
          )}

          {detay.kmKiyas && (
            <div className="inst" style={{ marginTop: 10 }}>
              <div className="inst-t">KM Kıyas — Derin Analiz</div>
              <div className="inst-row">
                <span className="inst-l">Numune ort.</span>
                <span className="inst-v">{tr(detay.kmKiyas.numuneOrt, 2)}</span>
              </div>
              <div className="inst-row">
                <span className="inst-l">Stok kartı</span>
                <span className="inst-v">{tr(detay.kmKiyas.stokKarti, 2)}</span>
              </div>
              <div className="inst-row">
                <span className="inst-l">Substrat geneli</span>
                <span className="inst-v">{tr(detay.kmKiyas.substratGeneli, 2)}</span>
              </div>
              <div className="inst-row">
                <span className="inst-l">{detay.kmKiyas.cariUnvan ?? 'Cari'} ort.</span>
                <span className="inst-v">{tr(detay.kmKiyas.cariOrt, 2)}</span>
              </div>
              {/* `k.alt` artık lotta cari, diğerlerinde sabit açıklama metni;
                  bu not yalnız kaynağın adını taşıyor. */}
              <div className="inst-note">Kaynak: {k.ust} — K13 zinciri</div>
            </div>
          )}

          {/* Aynı substratı veren tedarikçilerin karnesi (ref:695-696);
              numunenin kendi carisi vurgulanır. */}
          {detay.kmKiyas && detay.kmKiyas.cariKirilimi.length > 0 && (
            <div className="side-card">
              <div className="sect-t">Cari Bazında KM Ort.</div>
              <div className="mini-list" style={{ padding: '4px 0' }}>
                {detay.kmKiyas.cariKirilimi.map((c) => (
                  <div
                    className="mini-li"
                    key={c.cariUnvan}
                    style={
                      c.cariUnvan === detay.kmKiyas!.cariUnvan
                        ? { padding: '5px 12px', background: 'var(--tint)' }
                        : { padding: '5px 12px' }
                    }
                  >
                    {c.cariUnvan}
                    <span className="cell-sub" style={{ marginLeft: 6 }}>
                      {c.analizSayisi} analiz
                    </span>
                    <span className="vv">{tr(c.ortalama, 2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Referans sağ sütunu form kartı + `.inst` + `.side-card` ile
              bitiriyor (ref:733); "Son Analizler" YALNIZ panelde var (ref:565).
              Sol sütundaki `.ptab` zaten tüm geçmişi bastığı için sekmede
              ikinci bir son-3 listesi tekrar olurdu. */}
        </div>
      </div>
    </>
  )
}
